import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import CTAPreviewModal from './CTAPreviewModal';

interface CTA {
  id: string;
  type: 'ai_training' | 'agent_dev' | 'fractional_pm';
  header: string;
  description: string;
  button_text: string;
  icon: string;
  is_active: boolean;
  created_at: string;
}

export default function AdminCTAs() {
  const [ctas, setCTAs] = useState<CTA[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewCTA, setPreviewCTA] = useState<CTA | null>(null);

  useEffect(() => {
    fetchCTAs();
  }, []);

  const fetchCTAs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ctas')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCTAs(data || []);
    } catch (err) {
      console.error('Error fetching CTAs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load CTAs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this CTA?')) return;

    try {
      const { error } = await supabase
        .from('ctas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchCTAs();
    } catch (err) {
      console.error('Error deleting CTA:', err);
      alert('Failed to delete CTA');
    }
  };

  const handleToggleActive = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('ctas')
        .update({ is_active: !currentState })
        .eq('id', id);

      if (error) throw error;
      await fetchCTAs();
    } catch (err) {
      console.error('Error updating CTA:', err);
      alert('Failed to update CTA');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Call to Actions</h1>
        <Link
          to="/admin/ctas/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          <Plus className="h-5 w-5 mr-2" />
          New CTA
        </Link>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="bg-white divide-y divide-gray-200">
            {ctas.map((cta) => (
              <tr key={cta.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                    {cta.type}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{cta.header}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(cta.id, cta.is_active)}
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      cta.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {cta.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={() => setPreviewCTA(cta)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <Link
                      to={`/admin/ctas/${cta.id}`}
                      className="text-[#F98B3D] hover:text-[#e07a2c]"
                    >
                      <Pencil className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(cta.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CTAPreviewModal
        isOpen={!!previewCTA}
        onClose={() => setPreviewCTA(null)}
        cta={previewCTA}
      />
    </div>
  );
}